import { useState } from "react";
import { Alert, Card, Col, Row, Statistic, Tag, Spin, message, Button, Collapse } from "antd";
import {
  WarningOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  ToolOutlined,
  ExclamationCircleOutlined,
  ThunderboltOutlined,
  FieldTimeOutlined,
} from "@ant-design/icons";
import Header from "../_shared/components/Header";
import {
  useGetDashboardQuery,
  useRunSchedulingMutation,
  useRunCPSchedulingMutation,
  useGetEngineStatusQuery,
  useGetRecentScheduleRunFailuresQuery,
} from "../../../redux/api/scheduling";
import PreflightDrawer from "./PreflightDrawer";
import ScheduleRunsPanel from "../ScheduleRuns/ScheduleRunsPanel";
import type { SchedulerEngine } from "../../../interfaces/Scheduling.interface";

// Surface a recently-failed ScheduleRun at the top of the dashboard so the
// operator knows when a click on Run produced an error — even if Celery
// already cleared the engine-state flag. Considered "recent" if the run
// completed within the last hour.
const FAILED_RUN_WINDOW_MS = 60 * 60 * 1000;

export default function Dashboard() {
  const { data, isLoading } = useGetDashboardQuery({}, { refetchOnMountOrArgChange: true });
  const { data: engineStatus } = useGetEngineStatusQuery({}, { pollingInterval: 5000 });
  // Only the failures inside the window, not the full run history: the list is every run
  // with its solver_stats, ~14 MB of JSON, which this polled every 15s (PTB-6921).
  const { data: scheduleRuns } = useGetRecentScheduleRunFailuresQuery(FAILED_RUN_WINDOW_MS / 60_000, {
    pollingInterval: 15000,
  });
  const [runScheduling, { isLoading: isScheduling }] = useRunSchedulingMutation();
  const [runCPScheduling, { isLoading: isCPScheduling }] = useRunCPSchedulingMutation();
  const [drawerEngine, setDrawerEngine] = useState<SchedulerEngine | null>(null);
  const [dismissedFailedRunId, setDismissedFailedRunId] = useState<number | null>(null);

  // Find the most recent run that finished with status="failed" within the
  // last hour and hasn't been dismissed by the operator.
  const recentFailedRun = (() => {
    const runs: any[] = (scheduleRuns as any)?.results || (scheduleRuns as any) || [];
    if (!Array.isArray(runs) || runs.length === 0) return null;
    const cutoff = Date.now() - FAILED_RUN_WINDOW_MS;
    const failed = runs
      .filter((r: any) => r.status === "failed" && r.completed_at)
      .filter((r: any) => new Date(r.completed_at).getTime() >= cutoff)
      .sort((a: any, b: any) => new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime());
    const top = failed[0];
    if (!top) return null;
    if (dismissedFailedRunId === top.id) return null;
    return top;
  })();

  const heuristicRunning = engineStatus?.heuristic?.running || isScheduling;
  const cpSatRunning = engineStatus?.cp_sat?.running || isCPScheduling;
  const headroomRunning = engineStatus?.headroom?.running;

  const handleRunScheduling = async () => {
    try {
      const res: any = await runScheduling({}).unwrap();
      if (res?.task_id) {
        message.info("Heuristic scheduler started");
      } else {
        message.success("Scheduling completed");
      }
    } catch (err: any) {
      message.error(err?.data?.error || "Scheduling failed");
    }
  };

  const handleRunCPScheduling = async () => {
    try {
      const res: any = await runCPScheduling({}).unwrap();
      if (res?.task_id) {
        message.info("CP-SAT scheduler started");
      } else {
        message.success("CP-SAT scheduling completed");
      }
    } catch (err: any) {
      message.error(err?.data?.error || "CP-SAT scheduling failed");
    }
  };

  const handleDrawerContinue = (engine: SchedulerEngine) => {
    setDrawerEngine(null);
    if (engine === "heuristic") {
      void handleRunScheduling();
    } else if (engine === "cp_sat") {
      void handleRunCPScheduling();
    }
  };

  if (isLoading) return <Spin size="large" className="mt-20 mx-auto block" />;

  const d = data || {};

  const fmtDaysHours = (obj: any) => {
    if (!obj) return "0d 0h";
    const days = obj.days || 0;
    const hours = obj.hours || 0;
    return `${days}d ${hours}h`;
  };

  return (
    <div className="w-full p-4">
      <Header title="Dashboard" />

      {recentFailedRun && (
        <Alert
          type="error"
          showIcon
          closable
          onClose={() => setDismissedFailedRunId(recentFailedRun.id)}
          className="mb-4"
          message={
            <span>
              <strong>
                {recentFailedRun.engine_type === "cp_sat" ? "CP-SAT" :
                 recentFailedRun.engine_type === "headroom" ? "Headroom" : "Heuristic"}
                {" "}run failed
              </strong>
              {recentFailedRun.completed_at && (
                <span style={{ marginLeft: 8, fontWeight: "normal", opacity: 0.8 }}>
                  ({new Date(recentFailedRun.completed_at).toLocaleString()})
                </span>
              )}
            </span>
          }
          description={
            <div style={{ whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: 12 }}>
              {(recentFailedRun.error_message || "(no error message recorded)").slice(0, 600)}
              {recentFailedRun.error_message && recentFailedRun.error_message.length > 600 && (
                <span style={{ opacity: 0.6 }}> … (truncated; see Schedule Runs panel below for full text)</span>
              )}
            </div>
          }
        />
      )}

      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} md={8}>
          <Card>
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <Statistic
                title="Scheduling Engine"
                value={heuristicRunning ? "Running..." : "Ready"}
                prefix={<ThunderboltOutlined />}
                valueStyle={{ color: heuristicRunning ? "#faad14" : "#52c41a", fontSize: 20 }}
                style={{ minWidth: 130 }}
              />
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                loading={heuristicRunning}
                disabled={heuristicRunning}
                onClick={() => setDrawerEngine("heuristic")}
                style={{ background: "#52c41a", borderColor: "#52c41a" }}
              >
                {heuristicRunning ? "Running" : "Run"}
              </Button>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <Statistic
                title="CP-SAT Engine"
                value={cpSatRunning ? "Solving..." : "Ready"}
                prefix={<ThunderboltOutlined />}
                valueStyle={{ color: cpSatRunning ? "#faad14" : "#722ed1", fontSize: 20 }}
                style={{ minWidth: 130 }}
              />
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                loading={cpSatRunning}
                disabled={cpSatRunning}
                onClick={() => setDrawerEngine("cp_sat")}
                style={{ background: "#722ed1", borderColor: "#722ed1" }}
              >
                {cpSatRunning ? "Solving" : "Run"}
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={12} sm={8} md={6}>
          <Card>
            <Statistic title="Active Jobs" value={d.active_jobs || 0} prefix={<ClockCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card>
            <Statistic title="On Hold" value={d.on_hold_jobs || 0} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card>
            <Statistic
              title="Late Jobs"
              value={d.late_jobs || 0}
              valueStyle={{ color: d.late_jobs > 0 ? "#cf1322" : undefined }}
              prefix={d.late_jobs > 0 ? <WarningOutlined /> : undefined}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card>
            <Statistic
              title="At Risk"
              value={d.at_risk_jobs || 0}
              valueStyle={{ color: d.at_risk_jobs > 0 ? "#faad14" : undefined }}
              prefix={d.at_risk_jobs > 0 ? <ExclamationCircleOutlined /> : undefined}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card>
            <Statistic
              title="Job Days Late"
              value={fmtDaysHours(d.job_days_late)}
              prefix={<FieldTimeOutlined />}
              valueStyle={{
                color: d.job_days_late?.days || d.job_days_late?.hours ? "#cf1322" : undefined,
                fontSize: 20,
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card>
            <Statistic
              title="Job Days Early"
              value={fmtDaysHours(d.job_days_early)}
              prefix={<FieldTimeOutlined />}
              valueStyle={{
                color: d.job_days_early?.days || d.job_days_early?.hours ? "#52c41a" : undefined,
                fontSize: 20,
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card>
            <Statistic title="Active Staff" value={d.active_staff || 0} prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card>
            <Statistic title="Active Machines" value={d.active_machines || 0} prefix={<ToolOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card>
            <Statistic
              title="Total Exceptions"
              value={d.total_exceptions || 0}
              valueStyle={{ color: d.total_exceptions > 0 ? "#cf1322" : undefined }}
            />
          </Card>
        </Col>
      </Row>

      <PreflightDrawer
        open={drawerEngine !== null}
        engine={drawerEngine}
        onClose={() => setDrawerEngine(null)}
        onContinue={handleDrawerContinue}
        continueLoading={isScheduling || isCPScheduling}
      />

      {d.exception_breakdown && (
        <Card title="Exception Breakdown" className="mb-6">
          <div className="flex flex-wrap gap-3">
            {Object.entries(d.exception_breakdown).map(([type, count]: [string, any]) => (
              <Tag
                key={type}
                color={count > 0 ? "red" : "default"}
                className="text-sm py-1 px-3"
              >
                {type.replace(/_/g, " ").toUpperCase()}: {count}
              </Tag>
            ))}
          </div>
        </Card>
      )}

      <Collapse
        style={{ marginTop: 24 }}
        items={[
          {
            key: "schedule-runs",
            label: <span style={{ fontSize: 16, fontWeight: 600 }}>Schedule Runs</span>,
            children: <ScheduleRunsPanel />,
          },
        ]}
      />
    </div>
  );
}
